import * as THREE from 'three'
import { ThreeItemKeys, ThreeItemTypes } from '../../../constants/three'
import { get } from 'svelte/store'
import { focusOnObject } from '../../../stores/camera'
import { getColorFromTheme } from '../../../helpers/theme'
import { materialColors } from '../../../constants/theme'
import { createMutation, createQuery } from '@tanstack/svelte-query'
import { storeScene } from '../../../stores/scene'
import queryClient from '../../../constants/query-client'
import { canvasSelection, pruneMissingFromMultiSelection } from '../../../stores/canvas-selection'
import { restoreOriginalMaterial, updateSelectionBox } from '../../../helpers/selection-material'
import { PrinterService } from '../../../services'

const usePrinterQueries = () => {
  const getPrinter = (objectUuid: THREE.Object3D['uuid']) => {
    const foundPrinter = get(storeScene)?.getObjectByProperty('uuid', objectUuid)
    if (!foundPrinter) {
      throw new Error('printer not found')
    }

    return foundPrinter
  }

  const refreshPrinterZoneRendering = (printer: THREE.Object3D) => {
    const areaType: PrintableZoneType = printer.userData[ThreeItemKeys.AREA_TYPE] || 'none'
    const scene = get(storeScene)

    return PrinterService.showPrintableZone(scene, printer.uuid, areaType)
  }

  const refreshPrinterGridRendering = (printer: THREE.Object3D) => {
    const gridType: FloorGridType = printer.userData[ThreeItemKeys.GRID_TYPE] || 'none'
    const scene = get(storeScene)

    return PrinterService.showFloorZone(scene, printer.uuid, gridType)
  }

  const refreshOptionConstraintZonesRendering = (printer: THREE.Object3D) => {
    const scene = get(storeScene)

    return PrinterService.showOptionConstraintZones(scene, printer.uuid)
  }

  const refreshPrinterRendering = async (objectUuid: THREE.Object3D['uuid']) => {
    const printer = getPrinter(objectUuid)

    // Zones/grid are added as children of the printer group, so the selection
    // box must be recomputed once they render — otherwise it stays sized to the
    // bare printer mesh and ignores the (possibly much larger) printable zone/grid.
    await Promise.all([
      refreshPrinterZoneRendering(printer),
      refreshPrinterGridRendering(printer),
      refreshOptionConstraintZonesRendering(printer),
    ])

    if (get(canvasSelection)?.uuid === printer.uuid) {
      updateSelectionBox(printer)
    }
  }

  const invalidatePrinters = (objectUuid: THREE.Object3D['uuid']) => {
    queryClient.invalidateQueries({ queryKey: ['canvas', 'printers'] })
    queryClient.invalidateQueries({ queryKey: ['canvas', 'objects', objectUuid] })
    refreshPrinterRendering(objectUuid)
  }

  const usePrinter = (objectUuid: THREE.Object3D['uuid']) => createQuery(() => ({
    queryKey: ['canvas', 'printers', objectUuid],
    queryFn: () => getPrinter(objectUuid),
  }), () => queryClient)

  const getPrinterMaterial = (offset: number, forcedColor?: string) => {
    return new THREE.MeshStandardMaterial({
      color: getColorFromTheme(`--color-picker-${forcedColor ? forcedColor : materialColors[offset % materialColors.length]}-solid`),
      side: THREE.DoubleSide,
      metalness: 0.3,
      roughness: 0.3,
    })
  }

  const getColor = (colorName: string) => getColorFromTheme(`--color-picker-${colorName}-solid`)

  const updateObjectColor = (object: THREE.Object3D, color: string) => {
    object.traverse((child) => {
      if ('material' in child && !child.userData[ThreeItemKeys.TRANSIENT]) {
        (child.material as THREE.LineBasicMaterial | THREE.MeshStandardMaterial)?.color.set(getColor(color))
        child.userData.originalMaterial = (child.material as THREE.Material).toJSON()
      }
    })
  }

  const changeColorMutation = createMutation(() => ({
    mutationFn: ({ objectUuid, color }: { objectUuid: THREE.Object3D['uuid'], color: string }) => new Promise((resolve) => {
      const printer = getPrinter(objectUuid)
      const printerId = printer.userData[ThreeItemKeys.PRINTER_ID]
      printer.userData[ThreeItemKeys.PRINTER_COLOR] = color

      printer.traverse((child) => {
        switch (child.userData[ThreeItemKeys.ITEM_TYPE]) {
          case ThreeItemTypes.PRINTER_MESH:
            (child as THREE.Mesh).material = getPrinterMaterial(0, color)
            break
          case ThreeItemTypes.INNER_ZONE_RING:
          case ThreeItemTypes.OUTER_ZONE_RING:
            (child as THREE.Mesh).material = new THREE.MeshBasicMaterial({ color: getColor(color), side: THREE.DoubleSide })
        }
      })

      // Update color of all assigned files
      const scene = get(storeScene)
      scene?.traverse((object) => {
        if (object.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID] === printerId) {
          updateObjectColor(object, color)
        }
      })

      resolve(printer)
    }),
    onSuccess: (_data, { objectUuid }) => invalidatePrinters(objectUuid),
  }), () => queryClient)

  const changeVisibility = (visibility: boolean) => createMutation(() => ({
    mutationFn: (objectUuid: THREE.Object3D['uuid']) => new Promise((resolve) => {
      const printer = getPrinter(objectUuid)
      printer.visible = visibility
      resolve(printer)
    }),
    onSuccess: (_data, objectUuid) => invalidatePrinters(objectUuid),
  }), () => queryClient)

  const hideMutation = changeVisibility(false)

  const showMutation = changeVisibility(true)

  const removeMutation = createMutation(() => ({
    mutationFn: (objectUuid: THREE.Object3D['uuid']) => new Promise((resolve) => {
      const printer = getPrinter(objectUuid)
      const printerId = printer.userData[ThreeItemKeys.PRINTER_ID]
      const selectedElement = get(canvasSelection)

      if (selectedElement?.uuid === objectUuid) {
        restoreOriginalMaterial(selectedElement)
        canvasSelection.set(undefined)
      }

      if (printerId) {
        get(storeScene)?.traverse((object) => {
          if (object.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID] === printerId) {
            delete object.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID]
            delete object.userData[ThreeItemKeys.PRINTING_ORDER]
            object.traverse((child) => {
              if ('material' in child && !child.userData[ThreeItemKeys.TRANSIENT]) {
                (child.material as THREE.LineBasicMaterial | THREE.MeshStandardMaterial)?.color.set(new THREE.Color('lightgray'))
                child.userData.originalMaterial = (child.material as THREE.Material).toJSON()
              }
            })
          }
        })
      }

      printer?.parent?.remove(printer)

      resolve(true)
    }),
    onSuccess: () => {
      pruneMissingFromMultiSelection()
      queryClient.invalidateQueries({ queryKey: ['canvas', 'printers'] })
      queryClient.invalidateQueries({ queryKey: ['canvas'] })
    },
  }), () => queryClient)

  const renameMutation = createMutation(() => ({
    mutationFn: ({ objectUuid, name }: { objectUuid: THREE.Object3D['uuid'], name: string }) => new Promise((resolve) => {
      const printer = getPrinter(objectUuid)
      printer.name = name
      resolve(printer)
    }),
    onSuccess: (_data, { objectUuid }) => invalidatePrinters(objectUuid),
  }), () => queryClient)

  const updateAttributeMutation = createMutation(() => ({
    mutationFn: ({ objectUuid, object }: { objectUuid: THREE.Object3D['uuid'], object: Record<string, string | number | undefined | null> }) => new Promise((resolve) => {
      const printer = getPrinter(objectUuid)
      printer.userData = {
        ...printer.userData,
        ...object,
      }
      resolve(printer)
    }),
    onSuccess: (_data, { objectUuid }) => invalidatePrinters(objectUuid),
  }), () => queryClient)

  const changePrinterOptionMutation = createMutation(() => ({
    mutationFn: ({ objectUuid, optionKey, optionValue }: { objectUuid: THREE.Object3D['uuid'], optionKey: string, optionValue: string }) => new Promise((resolve) => {
      const printer = getPrinter(objectUuid)
      const currentOptions = printer.userData[ThreeItemKeys.PRINTER_OPTIONS] ?? {}
      printer.userData[ThreeItemKeys.PRINTER_OPTIONS] = {
        ...currentOptions,
        [optionKey]: optionValue,
      }
      resolve(printer)
    }),
    onSuccess: (_data, { objectUuid }) => invalidatePrinters(objectUuid),
  }), () => queryClient)

  const focusCamera = (objectUuid: THREE.Object3D['uuid']) => {
    const printer = getPrinter(objectUuid)
    if (printer) focusOnObject(printer)
  }

  return {
    usePrinter,
    changeColorMutation,
    changePrinterOptionMutation,
    hideMutation,
    showMutation,
    removeMutation,
    renameMutation,
    focusCamera,
    updateAttributeMutation,
  }
}

export default usePrinterQueries
